import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Landmark, Loader2, ShieldCheck, Download, FileText, CheckCircle2,
  AlertTriangle, Building2, Info, History, Banknote,
} from "lucide-react";

const eur = (cents) =>
  `€ ${((Number(cents) || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso || ""; } };
const todayPlus = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

/* ---------------- Configurazione creditore ---------------- */
const CreditorConfigCard = ({ config, onSaved }) => {
  const [form, setForm] = useState({
    creditor_name: "", creditor_id: "", creditor_iban: "", creditor_bic: "",
    scheme: "CORE", charge_gross: true,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (config) setForm((f) => ({ ...f, ...config })); }, [config]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.creditor_name.trim()) { toast.error("Inserisci il nome del creditore."); return; }
    if (!form.creditor_id.trim()) { toast.error("Inserisci il Creditor Identifier (SEPA CI)."); return; }
    setBusy(true);
    try {
      const r = await api.put("/admin/sepa-dd/config", form);
      toast.success("Configurazione salvata.");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="sepa-dd-config">
      <div className="flex items-center gap-2 mb-1">
        <Landmark className="h-4 w-4 text-[#007BFF]" />
        <h3 className="font-head text-lg font-semibold">Dati del creditore (la tua banca)</h3>
        {config?.configured
          ? <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="sepa-dd-config-ready"><CheckCircle2 className="h-3 w-3" />Configurato</span>
          : <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3" />Da configurare</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Questi dati vengono forniti dalla tua banca insieme al contratto di addebito diretto SEPA. Il
        <b> Creditor Identifier</b> (SEPA CI) è il codice che identifica te come creditore.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Nome creditore *</label>
          <Input data-testid="sepa-dd-creditor-name" value={form.creditor_name} onChange={(e) => set("creditor_name", e.target.value)} placeholder="Ragione sociale (es. Sedio SRL)" className="h-9 mt-1" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Creditor Identifier (SEPA CI) *</label>
          <Input data-testid="sepa-dd-creditor-id" value={form.creditor_id} onChange={(e) => set("creditor_id", e.target.value)} placeholder="IT66ZZZ..." className="h-9 mt-1 font-mono uppercase" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">IBAN del creditore *</label>
          <Input data-testid="sepa-dd-creditor-iban" value={form.creditor_iban} onChange={(e) => set("creditor_iban", e.target.value)} placeholder="IT60..." className="h-9 mt-1 font-mono uppercase" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">BIC / SWIFT (facoltativo)</label>
          <Input data-testid="sepa-dd-creditor-bic" value={form.creditor_bic} onChange={(e) => set("creditor_bic", e.target.value)} placeholder="ABCDITMM" className="h-9 mt-1 font-mono uppercase" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Schema mandato</label>
          <select data-testid="sepa-dd-scheme" value={form.scheme} onChange={(e) => set("scheme", e.target.value)}
            className="text-sm rounded-md border border-input bg-background px-2 py-1.5">
            <option value="CORE">CORE (standard)</option>
            <option value="B2B">B2B (tra imprese)</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid="sepa-dd-charge-gross" checked={form.charge_gross} onCheckedChange={(v) => set("charge_gross", v)} />
          <span className="text-sm">Incassa importo <b>IVA inclusa</b></span>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <Button data-testid="sepa-dd-config-save" onClick={save} disabled={busy} className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}Salva configurazione
        </Button>
      </div>
    </div>
  );
};

/* ---------------- Elenco incassi ---------------- */
const CollectionsCard = ({ configured, onGenerated }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [collDate, setCollDate] = useState(todayPlus(6));
  const [generating, setGenerating] = useState(false);
  const [rowBusy, setRowBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/sepa-dd/collections").then((r) => setData(r.data)).catch(() => setData({ collections: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cols = data?.collections || [];
  const eligible = cols.filter((c) => c.signed && c.iban_valid);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selTotal = cols.filter((c) => selected[c.building_id]).reduce((s, c) => s + c.amount_cents, 0);

  const toggleMandate = async (c) => {
    setRowBusy(c.building_id);
    try {
      await api.post("/admin/sepa-dd/mandate", { building_id: c.building_id, signed: !c.signed });
      toast.success(!c.signed ? "Mandato segnato come firmato." : "Mandato non più firmato.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita."); }
    finally { setRowBusy(""); }
  };

  const selectAllEligible = () => {
    const next = {};
    eligible.forEach((c) => { next[c.building_id] = true; });
    setSelected(next);
  };

  const generate = async () => {
    if (selectedIds.length === 0) { toast.error("Seleziona almeno un condominio."); return; }
    setGenerating(true);
    try {
      const r = await api.post("/admin/sepa-dd/generate",
        { building_ids: selectedIds, collection_date: collDate },
        { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `sepa_dd_${collDate}.xml`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      const cnt = r.headers["x-sedio-count"] || selectedIds.length;
      toast.success(`File XML generato: ${cnt} incassi. Caricalo sul portale della tua banca.`);
      setSelected({});
      load();
      onGenerated?.();
    } catch (e) {
      let detail = "Generazione non riuscita.";
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* noop */ }
      } else { detail = e?.response?.data?.detail || detail; }
      toast.error(detail);
    } finally { setGenerating(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="sepa-dd-collections">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Banknote className="h-4 w-4 text-emerald-600" />
        <h3 className="font-head text-lg font-semibold">Incassi da generare</h3>
        <span className="ml-auto text-xs text-muted-foreground">{cols.length} condomìni abbonati · {eligible.length} pronti</span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Segna quali mandati sono firmati, seleziona i condomìni e genera il file XML (pain.008) da caricare
        in home banking. Solo i condomìni con <b>mandato firmato</b> e <b>IBAN valido</b> sono incassabili.
      </p>

      {!data ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : cols.length === 0 ? (
        <div className="text-sm text-muted-foreground bg-secondary/40 rounded-md px-3 py-4 text-center">
          Nessun condominio abbonato disponibile per l'incasso.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-border rounded-md">
            <table className="w-full text-sm" data-testid="sepa-dd-collections-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-secondary/40">
                  <th className="py-2 px-3 w-8"></th>
                  <th className="py-2 px-3">Condominio</th>
                  <th className="py-2 px-3">Piano</th>
                  <th className="py-2 px-3">Importo</th>
                  <th className="py-2 px-3">IBAN debitore</th>
                  <th className="py-2 px-3">Sequenza</th>
                  <th className="py-2 px-3">Mandato</th>
                </tr>
              </thead>
              <tbody>
                {cols.map((c) => {
                  const canSelect = c.signed && c.iban_valid;
                  return (
                    <tr key={c.building_id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`sepa-dd-row-${c.building_id}`}>
                      <td className="py-2 px-3">
                        <input type="checkbox" data-testid={`sepa-dd-select-${c.building_id}`}
                          disabled={!canSelect} checked={!!selected[c.building_id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [c.building_id]: e.target.checked }))}
                          className="h-4 w-4 accent-[#007BFF] disabled:opacity-40" />
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-medium flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{c.name}</div>
                        <div className="text-[11px] text-muted-foreground">{[c.citta, c.code].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{c.option_label}</td>
                      <td className="py-2 px-3 font-semibold">{eur(c.amount_cents)}</td>
                      <td className="py-2 px-3">
                        <span className={`font-mono text-[12px] ${c.iban_valid ? "" : "text-red-600"}`}>{c.debtor_iban || "—"}</span>
                        {!c.iban_valid && c.debtor_iban && <div className="text-[10px] text-red-600">IBAN non valido</div>}
                        {!c.debtor_iban && <div className="text-[10px] text-red-600">IBAN mancante</div>}
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-secondary">{c.sequence_type}</span>
                      </td>
                      <td className="py-2 px-3">
                        <button data-testid={`sepa-dd-mandate-toggle-${c.building_id}`} disabled={rowBusy === c.building_id}
                          onClick={() => toggleMandate(c)}
                          className={`inline-flex items-center gap-1 text-[12px] font-medium hover:underline disabled:opacity-50 ${c.signed ? "text-emerald-600" : "text-muted-foreground"}`}>
                          {rowBusy === c.building_id ? <Loader2 className="h-3 w-3 animate-spin" /> : c.signed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                          {c.signed ? "Firmato" : "Segna firmato"}
                        </button>
                        {c.umr && <div className="text-[10px] text-muted-foreground font-mono">{c.umr}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-end gap-4 mt-4 pt-4 border-t border-border">
            <button onClick={selectAllEligible} data-testid="sepa-dd-select-all" className="text-[12px] font-medium text-[#007BFF] hover:underline">
              Seleziona tutti gli incassabili ({eligible.length})
            </button>
            <div className="flex-1" />
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground block mb-1">Data di incasso</label>
              <Input data-testid="sepa-dd-collection-date" type="date" value={collDate} min={todayPlus(0)}
                onChange={(e) => setCollDate(e.target.value)} className="h-9 w-44" />
            </div>
            <Button data-testid="sepa-dd-generate" onClick={generate} disabled={generating || selectedIds.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-md h-9">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Genera XML ({selectedIds.length}) · {eur(selTotal)}
            </Button>
          </div>
          {!configured && (
            <div className="mt-3 flex items-start gap-2 text-[12px] text-amber-700 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md px-3 py-2" data-testid="sepa-dd-not-configured-warn">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              Configura prima i dati del creditore (sopra) per poter generare il file.
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ---------------- Storico file generati ---------------- */
const BatchHistory = ({ refreshKey }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/sepa-dd/batches").then((r) => setRows(r.data?.batches || [])).catch(() => setRows([]));
  }, [refreshKey]);
  if (!rows) return null;
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="sepa-dd-batches">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-[#007BFF]" />
        <h3 className="font-head text-lg font-semibold">Storico file generati</h3>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Nessun file ancora generato.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((b) => (
            <div key={b.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2.5" data-testid={`sepa-dd-batch-${b.id}`}>
              <FileText className="h-4 w-4 text-[#007BFF] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium font-mono truncate">{b.filename}</div>
                <div className="text-[11px] text-muted-foreground">
                  Incasso {fmtDate(b.collection_date)} · {b.count} disposizioni · {b.scheme}
                  {b.skipped?.length > 0 && <span className="text-amber-600"> · {b.skipped.length} scartati</span>}
                </div>
              </div>
              <span className="font-head font-extrabold text-emerald-600">{eur(b.total_cents)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function SepaDirectDebit() {
  const [config, setConfig] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/admin/sepa-dd/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="sepa-dd-page">
      <div>
        <h1 className="font-head text-2xl font-extrabold tracking-tight flex items-center gap-2">
          <Landmark className="h-6 w-6 text-[#007BFF]" />Addebiti diretti SEPA (banca propria)
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Incassa gli abbonamenti direttamente con il <b>tuo Creditor Identifier</b>, senza Stripe né GoCardless.
          Genera il file XML delle disposizioni e caricalo sul portale della tua banca.
        </p>
      </div>

      <div className="flex items-start gap-2 text-[12.5px] text-[#0056B3] bg-[#007BFF]/8 border border-[#007BFF]/20 rounded-lg px-4 py-3" data-testid="sepa-dd-prereq-note">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          <b>Prerequisito bancario:</b> per usare questo canale devi avere un contratto di addebito diretto SEPA con
          la tua banca e un <b>Creditor Identifier (SEPA CI)</b>. Senza questi la banca rifiuterà il file.
          I mandati firmati dai condomìni vanno conservati da te (obbligo SEPA).
        </span>
      </div>

      <CreditorConfigCard config={config} onSaved={(c) => { setConfig(c); }} />
      <CollectionsCard configured={config?.configured} onGenerated={() => setRefreshKey((k) => k + 1)} />
      <BatchHistory refreshKey={refreshKey} />
    </div>
  );
}
